import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Heading } from "./NKDaily";
import { Crown, Sparkle, CheckCircle, Clock, XCircle, Square } from "@phosphor-icons/react";

export default function ChampionshipBank() {
  const [items, setItems] = useState([]);
  const [count, setCount] = useState(15);
  const [customPrompt, setCustomPrompt] = useState("");
  const [busy, setBusy] = useState(false);
  const [statusFilter, setStatusFilter] = useState("");
  const [selected, setSelected] = useState(() => new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const [creditError, setCreditError] = useState(null);

  const load = async () => {
    try {
      const r = await api.get("/admin/questions", {
        params: { track: "championship", status: statusFilter || undefined, limit: 500 },
      });
      setItems(r.data.questions || []);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [statusFilter]);

  const stats = useMemo(() => {
    const out = { total: items.length, approved: 0, pending: 0, rejected: 0, hard: 0, medium: 0 };
    items.forEach((q) => {
      out[q.status] = (out[q.status] || 0) + 1;
      if (q.difficulty === "hard") out.hard += 1;
      else if (q.difficulty === "medium") out.medium += 1;
    });
    return out;
  }, [items]);

  const generate = async () => {
    if (busy) return;
    setBusy(true);
    setCreditError(null);
    try {
      const { data } = await api.post("/admin/questions/ai/championship-generate", {
        count: Number(count),
        custom_prompt: customPrompt.trim() || null,
      });
      toast.success(`Elite batch inserted: ${data.inserted}/${data.received} questions`);
      setCustomPrompt("");
      load();
    } catch (e) {
      if (e.response?.status === 402) {
        setCreditError(e.response?.data?.detail || "AI credits exhausted.");
      } else {
        toast.error(e.response?.data?.detail || "Generation failed");
      }
    } finally { setBusy(false); }
  };

  const setStatus = async (qid, status) => {
    try {
      await api.patch(`/admin/questions/${qid}`, { status });
      toast.success(`Marked ${status}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const toggleOne = (qid) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(qid)) next.delete(qid); else next.add(qid);
      return next;
    });
  };
  const selectAllPending = () => {
    setSelected(new Set(items.filter((q) => q.status === "pending").map((q) => q.qid)));
  };
  const selectAllVisible = () => {
    setSelected(new Set(items.map((q) => q.qid)));
  };
  const clearSelection = () => setSelected(new Set());

  const bulkSetStatus = async (status) => {
    if (bulkBusy || selected.size === 0) return;
    if (!window.confirm(`${status === "approved" ? "Approve" : "Reject"} ${selected.size} selected question${selected.size === 1 ? "" : "s"}?`)) return;
    setBulkBusy(true);
    try {
      const { data } = await api.post("/admin/questions/bulk-status", {
        qids: Array.from(selected), status,
      });
      toast.success(`${data.modified} question${data.modified === 1 ? "" : "s"} marked ${status}`);
      clearSelection();
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Bulk update failed");
    } finally { setBulkBusy(false); }
  };

  return (
    <div className="space-y-6">
      <Heading
        title="Championship Question Bank"
        subtitle="Dedicated elite-tier bank used by the Day-31 International Championship. The picker pulls only from this bank during championships — they never see general-pool questions."
      />

      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3" data-testid="champ-bank-stats">
        <Stat label="Total" value={stats.total} icon={<Crown size={12} weight="fill" className="text-nk-gold" />} />
        <Stat label="Approved" value={stats.approved || 0} accent="ok" icon={<CheckCircle size={12} />} />
        <Stat label="Pending" value={stats.pending || 0} accent="warn" icon={<Clock size={12} />} />
        <Stat label="Hard" value={stats.hard} accent="bad" />
        <Stat label="Medium" value={stats.medium} />
      </div>

      {creditError && (
        <div className="nk-card p-4 border-l-2 border-nk-warn bg-nk-warn/5" data-testid="ai-credits-banner">
          <p className="eyebrow text-nk-warn mb-1">⚠ AI credits exhausted</p>
          <p className="text-sm text-nk-text">{creditError}</p>
          <p className="text-[11px] text-nk-mute mt-2">
            Open the user menu in the top-right of the Emergent workspace → <strong>Profile → Universal Key → Add Balance</strong>. After top-up, click "Generate" again here.
          </p>
        </div>
      )}

      <div className="nk-card p-5 sm:p-6 border-l-2 border-nk-gold">
        <div className="flex items-center gap-2 mb-3">
          <Sparkle size={14} weight="fill" className="text-nk-gold" />
          <p className="eyebrow text-nk-gold">Generate elite batch</p>
        </div>
        <p className="text-xs text-nk-mute mb-4 max-w-2xl">
          Uses a tuned prompt: 75% hard / 25% medium, 50% global elite knowledge, 50% Pan-African elite. Every question must defeat a casual player.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-[120px_1fr_auto] gap-3 items-end">
          <div>
            <label className="text-[10px] text-nk-mute uppercase tracking-wider">How many?</label>
            <input type="number" min={1} max={50} value={count}
                   onChange={(e) => setCount(e.target.value)}
                   data-testid="champ-gen-count"
                   className="w-full mt-1 bg-nk-bg border border-nk-line px-3 py-2 text-sm rounded-sm focus:border-nk-gold outline-none" />
          </div>
          <div>
            <label className="text-[10px] text-nk-mute uppercase tracking-wider">Extra steering (optional)</label>
            <input type="text" value={customPrompt}
                   onChange={(e) => setCustomPrompt(e.target.value)}
                   placeholder='e.g. "Focus on African Nobel laureates and 2020s tech IPOs"'
                   maxLength={500}
                   data-testid="champ-gen-prompt"
                   className="w-full mt-1 bg-nk-bg border border-nk-line px-3 py-2 text-sm rounded-sm focus:border-nk-gold outline-none" />
          </div>
          <button onClick={generate} disabled={busy}
                  data-testid="champ-gen-submit"
                  className="px-4 py-2.5 bg-nk-gold text-nk-bg font-medium rounded-sm hover:bg-nk-gold/90 disabled:opacity-40 inline-flex items-center gap-1.5 text-sm whitespace-nowrap">
            <Sparkle size={12} weight="fill" />
            {busy ? "Generating…" : "Generate"}
          </button>
        </div>
      </div>

      <div className="flex flex-wrap gap-1.5">
        {["", "approved", "pending", "rejected"].map((s) => (
          <button key={s || "all"} onClick={() => setStatusFilter(s)}
                  data-testid={`champ-filter-${s || "all"}`}
                  className={`px-3 py-1.5 text-xs uppercase tracking-wider rounded-sm border ${
                    statusFilter === s ? "bg-nk-accent text-nk-bg border-nk-accent" :
                    "text-nk-mute border-nk-line hover:border-nk-accent hover:text-nk-text"
                  }`}>
            {s || "All"}
          </button>
        ))}
        <div className="ml-auto flex items-center gap-1.5">
          <button onClick={selectAllPending}
                  data-testid="champ-select-pending"
                  className="text-[11px] px-2.5 py-1.5 border border-nk-line text-nk-mute hover:text-nk-text hover:border-nk-warn rounded-sm">
            Select all pending
          </button>
          <button onClick={selectAllVisible}
                  data-testid="champ-select-all"
                  className="text-[11px] px-2.5 py-1.5 border border-nk-line text-nk-mute hover:text-nk-text rounded-sm">
            Select all visible
          </button>
        </div>
      </div>

      {selected.size > 0 && (
        <div className="nk-card p-3 border-l-2 border-nk-accent flex flex-wrap items-center gap-3 sticky top-16 z-20 bg-nk-surface/95 backdrop-blur-sm"
             data-testid="champ-bulk-toolbar">
          <p className="text-sm font-medium">
            <span className="text-nk-accent font-mono">{selected.size}</span> selected
          </p>
          <button onClick={clearSelection}
                  data-testid="champ-bulk-clear"
                  className="text-[11px] px-2 py-1 text-nk-mute hover:text-nk-text">
            Clear
          </button>
          <div className="ml-auto flex items-center gap-2">
            <button onClick={() => bulkSetStatus("approved")} disabled={bulkBusy}
                    data-testid="champ-bulk-approve"
                    className="text-xs px-3 py-1.5 bg-nk-accent text-nk-bg font-medium rounded-sm hover:bg-nk-accent/90 disabled:opacity-40 inline-flex items-center gap-1.5">
              <CheckCircle size={12} weight="fill" />
              {bulkBusy ? "Working…" : `Approve ${selected.size}`}
            </button>
            <button onClick={() => bulkSetStatus("rejected")} disabled={bulkBusy}
                    data-testid="champ-bulk-reject"
                    className="text-xs px-3 py-1.5 border border-nk-bad/60 text-nk-bad rounded-sm hover:bg-nk-bad/10 disabled:opacity-40 inline-flex items-center gap-1.5">
              <XCircle size={12} weight="fill" />
              Reject {selected.size}
            </button>
          </div>
        </div>
      )}

      <div className="space-y-2">
        {items.length === 0 && (
          <div className="nk-card p-8 text-center">
            <Crown size={28} weight="duotone" className="text-nk-gold/60 mx-auto mb-3" />
            <p className="text-sm text-nk-mute">No championship-track questions yet.</p>
            <p className="text-[11px] text-nk-dim mt-1">Use the generator above to seed the elite bank.</p>
          </div>
        )}
        {items.map((q) => {
          const isSelected = selected.has(q.qid);
          return (
          <div key={q.qid} className={`nk-card p-4 transition-colors ${isSelected ? "border-nk-accent bg-nk-accent/5" : ""}`}>
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="flex items-start gap-3 flex-1 min-w-0">
                <button onClick={() => toggleOne(q.qid)}
                        data-testid={`champ-check-${q.qid}`}
                        className={`mt-0.5 shrink-0 ${isSelected ? "text-nk-accent" : "text-nk-mute hover:text-nk-text"}`}
                        aria-label={isSelected ? "Deselect" : "Select"}>
                  {isSelected ? <CheckCircle size={18} weight="fill" /> : <Square size={18} weight="regular" />}
                </button>
                <p className="font-medium text-nk-text max-w-3xl">{q.body}</p>
              </div>
              <div className="flex items-center gap-1.5 shrink-0">
                <Pill text={q.difficulty || "—"} tone={q.difficulty === "hard" ? "bad" : "warn"} />
                <Pill text={q.country_tag || "ALL"} tone="mute" />
                <Pill text={q.status || "pending"} tone={
                  q.status === "approved" ? "ok" : q.status === "rejected" ? "bad" : "warn"
                } />
              </div>
            </div>
            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-1.5 mt-3">
              {(q.options || []).map((o, idx) => (
                <li key={idx} className={`text-xs px-2.5 py-1.5 border rounded-sm ${
                  idx === q.answer_index ? "border-nk-accent text-nk-accent bg-nk-accent/5" :
                  "border-nk-line/40 text-nk-mute"
                }`}>
                  {idx === q.answer_index && "✓ "}{o}
                </li>
              ))}
            </ul>
            <div className="mt-3 flex items-center gap-2 text-[11px] text-nk-dim">
              <span className="font-mono">{q.category || "Uncategorized"}</span>
              <span>·</span>
              <span className="font-mono">{q.source || "manual"}</span>
              <div className="ml-auto flex gap-1.5">
                {q.status !== "approved" && (
                  <button onClick={() => setStatus(q.qid, "approved")}
                          data-testid={`champ-approve-${q.qid}`}
                          className="text-[11px] px-2 py-1 border border-nk-accent/50 text-nk-accent rounded-sm hover:bg-nk-accent/10">
                    <CheckCircle size={11} className="inline" /> Approve
                  </button>
                )}
                {q.status !== "rejected" && (
                  <button onClick={() => setStatus(q.qid, "rejected")}
                          data-testid={`champ-reject-${q.qid}`}
                          className="text-[11px] px-2 py-1 border border-nk-bad/50 text-nk-bad rounded-sm hover:bg-nk-bad/10">
                    <XCircle size={11} className="inline" /> Reject
                  </button>
                )}
              </div>
            </div>
          </div>
          );
        })}
      </div>
    </div>
  );
}

function Stat({ label, value, accent, icon }) {
  const color = accent === "ok" ? "text-nk-accent" : accent === "warn" ? "text-nk-warn" : accent === "bad" ? "text-nk-bad" : "text-nk-text";
  return (
    <div className="nk-card p-3">
      <p className="text-[10px] uppercase tracking-wider text-nk-dim flex items-center gap-1">{icon} {label}</p>
      <p className={`font-display text-2xl mt-1 ${color}`}>{value}</p>
    </div>
  );
}

function Pill({ text, tone }) {
  const map = {
    ok: "bg-nk-accent/15 text-nk-accent",
    warn: "bg-nk-warn/15 text-nk-warn",
    bad: "bg-nk-bad/15 text-nk-bad",
    mute: "bg-nk-line/40 text-nk-mute",
  };
  return (
    <span className={`text-[9px] px-1.5 py-0.5 rounded-sm uppercase tracking-wider font-mono ${map[tone] || map.mute}`}>
      {text}
    </span>
  );
}
